import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { init, axiosInit, AxiosPiniaPlugin, InjectedDataPlugin } from "@bloglovin/vue-framework";
// Register deployatron-owned inspire routine types BEFORE any
// engine code resolves a routine name. The import has side
// effects — each routine module self-registers via
// Routine.register(...) at module load. See
// src/Web/scripts/inspire-routines/index.js.
import "@/inspire-routines";
import pages from "@/pages";
import { useGeneralStore } from "@/stores/general-store";
import { useProjectAuditPresenceStore } from "@/stores/project-audit-presence-store";
import { applyQ3poTheme } from "@/themes/apply-q3po-theme";
import { registerCallIncidentLog } from "@/helpers/callIncidentLog.mjs";
// Engine boot lives in each page's created() hook (Rule 1 of
// /home/vince/inspire/INSPIRE_APP_RULES.md). main-vue is shared by
// every entry bundle and runs before the page knows what it needs;
// putting App.create here would either force-create on pages that
// don't use the engine (Login) or fight the page-owns-the-app rule.

import "@/styles/base.scss";
import VCL, { useStyleStore } from "@vcl";

axiosInit(window.blclient.data.JWT, window.blclient.data.links.api);
registerCallIncidentLog();

const initializationFunction = async function (vuePlugins, piniaProps) {
  const piniaInstance = vuePlugins.pinia;
  for (const store of Object.keys(piniaProps)) {
    let piniaStoreFileName = store.replace(/^use/, "").replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    await import(`@/stores/${piniaStoreFileName}`).then(
      resolvedStoreFile => {
        const storeInstance = resolvedStoreFile[store](piniaInstance);
        storeInstance.$injectCustomProperties(piniaProps[store]);
      },
    );
  }

  const generalStore = useGeneralStore(piniaInstance);
  useProjectAuditPresenceStore(piniaInstance).start();
  const styleStore = useStyleStore(piniaInstance);

  applyQ3poTheme(styleStore);

  generalStore.setApps(window.blclient.data.apps);
};

const pinia = createPinia();
pinia.use(AxiosPiniaPlugin);
pinia.use(InjectedDataPlugin);
// Engine EntityStore + FeedStore opt in to localStorage persistence
// via `persist: true` on their `defineStore` calls (see inspire's
// EntityStore.js / FeedStore.js). Each store namespace (per app +
// entityType / feed id) becomes its own localStorage key so a
// reload rehydrates without round-tripping through /entities and
// /feeds for every visible row. CLAUDE.md's "don't persist whole
// stores" guideline is for app-level UI stores; the engine stores
// ARE the data — there's no transient field to filter out.
pinia.use(piniaPluginPersistedstate);

const plugins = {
  pinia: pinia,
  vcl: VCL,
};

init(process.env.NODE_ENV, process.env.PROJECT_NAME, pages, plugins, initializationFunction);
